<template>
  <div class="erp-customer-list">
    <!-- ERP 客户关联关系（企业微信手机端） -->
    <mobile-list-header>
      <!-- 第一行：搜索框占满整行，左输入框 + 右搜索按钮 -->
      <div class="search-bar">
        <div class="search-input">
          <yq-icon class="icon" name="element-ui_search"/>
          <input v-model="searchInfo.searchKey" placeholder="客户名称/编码/联系电话" @keyup.enter="resetSearch"/>
          <yq-icon v-if="searchInfo.searchKey" class="icon clear" name="element-ui_close" @click="clearKey"/>
        </div>
        <div class="search-btn" @click="resetSearch">搜索</div>
      </div>
      <!-- 第二行：查询条件，无背景色，超过一屏横向滑动 -->
      <div class="filter-row">
        <mobile-condition-select v-model="searchInfo.parentId" :data="customerCategory"
                                 label-column="fullName" code-column="typeId" type="tree"
                                 placeholder="客户分类" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.searchType" :data="relationOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="小程序关联" @change="resetSearch"/>
        <mobile-condition-select v-model="searchInfo.status" :data="relationOptions"
                                 label-column="label" code-column="value" type="list"
                                 placeholder="报价工具关联" @change="resetSearch"/>
      </div>
    </mobile-list-header>

    <mobile-list :loading="loading" :has-more="searchInfo.hasMore" :query-list="queryList"
                 :refresh="resetSearch" ref="mobileList">
      <list-card class="customer-item" v-for="item in dataList" :key="item.typeId" :label="item.fullName">
        <!-- 头像 -->
        <template #icon>
          <div class="customer-avatar">
            <img v-if="item.avatar" :src="$getImageUrl(item.avatar)" alt=""/>
            <yq-icon v-else name="element-ui_user"/>
          </div>
        </template>
        <!-- 关联状态：小程序 / AI 报价工具 -->
        <div class="relation-tags">
          <span class="relation-tag" :class="item.wxUnionId ? 'on' : 'off'">
            小程序{{ item.wxUnionId ? "已关联" : "未关联" }}
          </span>
          <span class="relation-tag" :class="item.xbotId ? 'on' : 'off'">
            报价工具{{ item.xbotId ? "已关联" : "未关联" }}
          </span>
        </div>
        <data-column label="客户编号">{{ item.userCode || "—" }}</data-column>
        <data-column label="小程序手机号">{{ item.customerPhone || "—" }}</data-column>
        <data-column label="微信名称">{{ item.customerName || "—" }}</data-column>
        <data-column label="AI报价客户名">
          <span class="xbot-name">{{ item.customerXbotName || "—" }}</span>
          <span class="relation-handler" :class="{danger: !!item.customerXbotName}"
                @click.stop="toCreateRelation(item)">
            {{ item.customerXbotName ? "重新关联" : "关联客户" }}
          </span>
        </data-column>
      </list-card>
      <div v-if="!loading && dataList.length === 0" class="empty">暂无客户数据</div>
    </mobile-list>

    <!-- 选择 AI 报价客户 -->
    <mobile-drawer v-model="showXbotCustomers" title="选择AI报价客户">
      <div v-if="showXbotCustomers" class="xbot-drawer">
        <mobile-list-header>
          <div class="search-bar">
            <div class="search-input">
              <yq-icon class="icon" name="element-ui_search"/>
              <input v-model="xbotSearchInfo.searchKey" placeholder="客户名称" @keyup.enter="resetSearchXbot"/>
              <yq-icon v-if="xbotSearchInfo.searchKey" class="icon clear" name="element-ui_close"
                       @click="clearXbotKey"/>
            </div>
            <div class="search-btn" @click="resetSearchXbot">搜索</div>
          </div>
        </mobile-list-header>
        <mobile-list :loading="xbotLoading" :has-more="xbotSearchInfo.hasMore"
                     :query-list="queryListXbot" ref="xbotList">
          <list-card class="xbot-item" v-for="item in xbotDataList" :key="item.xbotId || item.id"
                     :label="item.customerName || '—'" @click="createRelation(item)">
            <template #icon>
              <div class="customer-avatar small">
                <img v-if="item.avatar" :src="$getImageUrl(item.avatar)" alt=""/>
                <yq-icon v-else name="element-ui_user"/>
              </div>
            </template>
            <template #desc>
              <yq-icon class="item-arrow" name="element-ui_arrow-right"/>
            </template>
          </list-card>
          <div v-if="!xbotLoading && xbotDataList.length === 0" class="empty">暂无AI报价客户</div>
        </mobile-list>
      </div>
    </mobile-drawer>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import erpCustomerApi from "@/api/dz-ai/erpCustomerRelationApi";
import MobileList from "@/components/qywx/layout/MobileList.vue";
import MobileListHeader from "@/components/qywx/layout/MobileListHeader.vue";
import MobileConditionSelect from "@/components/qywx/data/MobileConditionSelect.vue";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import MobileDrawer from "@/components/qywx/data/MobileDrawer.vue";

// 后端 searchType / status：1 已关联，2 未关联
const RELATION_OPTIONS = [
  {value: 1, label: "已关联"},
  {value: 2, label: "未关联"},
];

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "ErpCustomerRelationQW",
  components: {
    YqIcon, DataColumn, ListCard, MobileConditionSelect, MobileListHeader, MobileList, MobileDrawer
  },
  data() {
    return {
      loading: false,
      searchInfo: {
        searchKey: "",
        parentId: "",
        searchType: "",
        status: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true, // 初始为 true，列表挂载后由无限滚动自动触发首次查询
      },
      dataList: [],
      customerCategory: [],
      relationOptions: RELATION_OPTIONS,
      // AI 报价客户选择抽屉
      showXbotCustomers: false,
      currentData: {},
      xbotSearchInfo: {
        searchKey: "",
        pageNumber: 0,
        pageSize: 20,
        hasMore: true,
      },
      xbotLoading: false,
      xbotDataList: [],
    };
  },
  created() {
    erpCustomerApi.queryCategory().then(res => this.customerCategory = res || []);
  },
  methods: {
    queryList() {
      this.loading = true;
      this.searchInfo.pageNumber++;
      erpCustomerApi.queryErpCustomerList({
        ...this.cleanParams(),
        pageNumber: this.searchInfo.pageNumber,
        pageSize: this.searchInfo.pageSize,
      }).then(res => {
        this.dataList = this.dataList.concat(res.records || []);
        this.searchInfo.hasMore = res.pageCount > this.searchInfo.pageNumber;
        this.loading = false;
        this.$refs.mobileList.hideRefreshTitle();
      }).catch(() => {
        this.loading = false;
      })
    },
    resetSearch() {
      this.searchInfo.pageNumber = 0;
      this.dataList = [];
      this.searchInfo.hasMore = true;
      this.queryList();
    },
    clearKey() {
      this.searchInfo.searchKey = "";
      this.resetSearch();
    },
    /** 空值不提交，避免空串绑到后端 Integer（searchType/status） */
    cleanParams() {
      const params = {};
      ["searchKey", "parentId", "searchType", "status"].forEach(key => {
        const value = this.searchInfo[key];
        if (value !== "" && value !== null && value !== undefined) {
          params[key] = value;
        }
      });
      return params;
    },
    // 打开 AI 报价客户选择框。
    // hasMore 先置 false 禁用 MobileList 挂载时的自动首查（抽屉内高度可能为 0），
    // 由这里显式查第一页；响应回来后再按 pageCount 决定是否继续滚动加载，避免重复请求。
    toCreateRelation(data) {
      this.currentData = data;
      this.xbotSearchInfo.searchKey = "";
      this.xbotSearchInfo.pageNumber = 0;
      this.xbotSearchInfo.hasMore = false;
      this.xbotDataList = [];
      this.showXbotCustomers = true;
      this.queryListXbot();
    },
    createRelation(data) {
      erpCustomerApi.setXbotId({id: this.currentData.id, xbotId: data.xbotId}).then(() => {
        this.showXbotCustomers = false;
        this.resetSearch();
      })
    },
    resetSearchXbot() {
      this.xbotSearchInfo.pageNumber = 0;
      this.xbotDataList = [];
      this.xbotSearchInfo.hasMore = true;
      this.queryListXbot();
    },
    clearXbotKey() {
      this.xbotSearchInfo.searchKey = "";
      this.resetSearchXbot();
    },
    queryListXbot() {
      this.xbotLoading = true;
      this.xbotSearchInfo.pageNumber++;
      const params = {pageNumber: this.xbotSearchInfo.pageNumber, pageSize: this.xbotSearchInfo.pageSize};
      if (this.xbotSearchInfo.searchKey) {
        params.searchKey = this.xbotSearchInfo.searchKey;
      }
      erpCustomerApi.queryXbotCustomerList(params).then(res => {
        this.xbotDataList = this.xbotDataList.concat(res.records || []);
        this.xbotSearchInfo.hasMore = res.pageCount > this.xbotSearchInfo.pageNumber;
        this.xbotLoading = false;
      }).catch(() => {
        this.xbotLoading = false;
      })
    }
  }
};
</script>

<style lang="scss" scoped>
.erp-customer-list{
  height: 100%;
  display: flex;
  flex-direction: column;
}
// 第一行：搜索框占满整行，左输入框 + 右搜索按钮
.search-bar{
  display: flex;
  align-items: center;
  height: 44px;
  padding-left: 12px;
  border-radius: 999px;
  background: var(--yq-background-color);
  overflow: hidden;
  .search-input{
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    height: 100%;
    padding-right: 12px;
    .icon{
      flex: none;
      color: var(--yq-font-color-desc);
      font-size: var(--yq-font-size-mini-mobile);
      &.clear{
        margin-left: 6px;
      }
    }
    input{
      flex: 1;
      min-width: 0;
      height: 100%;
      margin-left: 6px;
      border: none;
      outline: none;
      background: transparent;
      font-size: var(--yq-font-size-mini-mobile);
      color: var(--yq-font-color-base);
      &::placeholder{
        color: var(--yq-font-color-desc);
      }
    }
  }
  .search-btn{
    flex: none;
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 18px;
    background: var(--el-color-primary);
    color: #FFF;
    font-size: var(--yq-font-size-mini-mobile);
  }
}
// 第二行：查询条件，无背景色，超过一屏横向滑动
.filter-row{
  display: flex;
  gap: 18px;
  margin-top: 10px;
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar{
    display: none;
  }
  > *{
    flex: none;
  }
}
// 头像在主列表和「选择AI报价客户」抽屉里共用，必须放在页面级
// （放在 .customer-item 下时，抽屉的 .xbot-item 匹配不到，图片会按原图尺寸撑开）
.customer-avatar{
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin-right: 10px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--yq-background-color);
  img{
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  .yq-icon{
    font-size: 28px;
    color: var(--yq-font-color-desc);
  }
  &.small{
    width: 44px;
    height: 44px;
    margin-right: 8px;
  }
}
.customer-item{
  .relation-tags{
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 10px 0 4px;
  }
  .relation-tag{
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    &.on{
      color: var(--el-color-success);
      background: var(--el-color-success-light-9);
    }
    &.off{
      color: var(--el-color-info);
      background: var(--el-fill-color-light);
    }
  }
  .xbot-name{
    margin-right: 8px;
  }
  .relation-handler{
    display: inline-block;
    padding: 2px 8px;
    border: 1px solid var(--el-color-primary);
    border-radius: 4px;
    color: var(--el-color-primary);
    font-size: var(--yq-font-size-mini-mobile);
    &.danger{
      border-color: var(--el-color-danger);
      color: var(--el-color-danger);
    }
  }
}
// AI 报价客户选择抽屉
.xbot-drawer{
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}
.xbot-item{
  :deep(.card-title .desc){
    display: flex;
    align-items: center;
  }
  .item-arrow{
    color: var(--yq-font-color-desc);
    font-size: var(--yq-font-size-mini-mobile);
  }
}
.empty{
  padding: 30px 0;
  text-align: center;
  font-size: var(--yq-font-size-mini-mobile);
  color: var(--yq-font-color-desc);
}
</style>
